import React, { useState } from 'react';
import { X, Trophy, Megaphone, CheckCircle2, Youtube, Share2, Bell, Gift } from 'lucide-react';
import { clubhouseApi } from '../../utils/clubhouseApi';
import './MemberModal.css';



// Icon mapping
const ICON_MAP = {
  trophy: Trophy,
  megaphone: Megaphone,
  bell: Bell,
  gift: Gift,
  checkCircle: CheckCircle2
};

export const MemberModal = ({ modal, onClose, onDismiss }) => {
  const [isClosing, setIsClosing] = useState(false);

  const handleClose = async (dismissForever = false) => {
    setIsClosing(true);
    
    // Animate out
    setTimeout(async () => {
      if (dismissForever) {
        // Mark as viewed in backend
        try {
          await clubhouseApi.post(`/api/clubhouse/modals/${modal.id}/view`);
          onDismiss(modal.id);
        } catch (error) {
          console.error('Error dismissing modal:', error);
        }
      }
      onClose();
    }, 200);
  };

  const handlePrimaryCTA = async () => {
    const cta = modal.primary_cta;
    
    if (cta.action_type === 'youtube' && cta.url) {
      window.open(cta.url, '_blank', 'noopener,noreferrer');
    } else if (cta.action_type === 'link' && cta.url) {
      window.open(cta.url, '_blank', 'noopener,noreferrer');
    }
    
    await handleClose(true);
  };

  const handleSecondaryCTA = async () => {
    const cta = modal.secondary_cta;
    
    if (cta.action_type === 'share') {
      // Try native share API
      if (navigator.share) {
        try {
          await navigator.share({
            title: 'Join Golf Nuts Clubhouse',
            text: 'Check out the Golf Nuts Clubhouse - exclusive contests, giveaways, and a community of golf enthusiasts!',
            url: window.location.origin + '/clubhouse'
          });
        } catch (error) {
          if (error.name !== 'AbortError') {
            console.error('Error sharing:', error);
            fallbackCopyLink();
          }
        }
      } else {
        fallbackCopyLink();
      }
    } else if (cta.url) {
      window.open(cta.url, '_blank', 'noopener,noreferrer');
    }
    
    await handleClose(true);
  };

  const fallbackCopyLink = () => {
    const link = window.location.origin + '/clubhouse';
    navigator.clipboard.writeText(link).then(() => {
      alert('Link copied to clipboard!');
    }).catch(err => {
      console.error('Failed to copy:', err);
    });
  };

  const IconComponent = ICON_MAP[modal.icon] || Megaphone;

  return (
    <>
      <div 
        className={`modal-overlay ${isClosing ? 'closing' : ''}`}
        onClick={() => handleClose(false)}
      />
      <div className={`member-modal ${isClosing ? 'closing' : ''}`}>
        {/* Header Banner */}
        <div className="modal-header-banner">
          <IconComponent size={20} className="banner-icon" />
          <span>{modal.title}</span>
        </div>

        {/* Close Button */}
        <button 
          className="modal-close-btn"
          onClick={() => handleClose(false)}
          aria-label="Close"
        >
          <X size={20} />
        </button>

        {/* Body Content */}
        <div className="modal-body">
          <h2 className="modal-headline">{modal.headline}</h2>
          
          <p className="modal-body-text">{modal.body_text}</p>

          {/* Progress Checklist */}
          {modal.show_progress_checklist && (
            <div className="progress-checklist">
              <div className="checklist-item">
                <CheckCircle2 size={20} className="check-icon" />
                <span>{modal.member_threshold}+ Clubhouse Members</span>
              </div>
              <div className="checklist-item">
                <CheckCircle2 size={20} className="check-icon" />
                <span>{modal.youtube_threshold}+ YouTube Subscribers</span>
              </div>
            </div>
          )}

          {/* Community Callout Card */}
          <div className="community-callout">
            <h3>{modal.community_callout_title}</h3>
            <p>{modal.community_callout_text}</p>
          </div>

          {/* CTA Buttons */}
          <div className="modal-cta-buttons">
            <button 
              className="cta-primary"
              onClick={handlePrimaryCTA}
            >
              {modal.primary_cta.action_type === 'youtube' && <Youtube size={18} />}
              <span>{modal.primary_cta.text}</span>
            </button>
            
            <button 
              className="cta-secondary"
              onClick={handleSecondaryCTA}
            >
              {modal.secondary_cta.action_type === 'share' && <Share2 size={18} />}
              <span>{modal.secondary_cta.text}</span>
            </button>
          </div>
        </div>

        {/* Footer */}
        <div className="modal-footer">
          <button 
            className="footer-close-link"
            onClick={() => handleClose(true)}
          >
            Maybe later
          </button>
        </div>
      </div>
    </>
  );
};
